import React, { useMemo, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import {
    Box,
    Button,
    Chip,
    Container,
    Divider,
    IconButton,
    Paper,
    Stack,
    TextField,
    Typography,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CalculateOutlinedIcon from "@mui/icons-material/CalculateOutlined";
import BusinessCenterOutlinedIcon from "@mui/icons-material/BusinessCenterOutlined";
import AccountBalanceOutlinedIcon from "@mui/icons-material/AccountBalanceOutlined";
import ArrowOutwardIcon from "@mui/icons-material/ArrowOutward";
import RemoveIcon from "@mui/icons-material/Remove";
import AddIcon from "@mui/icons-material/Add";

import { sectionSubtitleSx, sectionTitleSx } from "./homeStyles";

const MIN_BINS = 1;
const MAX_BINS = 100;

const PRICING_PLANS = [
    {
        title: "Базовый",
        minBins: 1,
        maxBins: 1,
        pricePerBin: 400000,
        description: "Для организаций, которым требуется подключение одного БИН.",
    },
    {
        title: "Стандарт",
        minBins: 2,
        maxBins: 9,
        pricePerBin: 350000,
        description: "Подходит для нескольких организаций в одной структуре.",
    },
    {
        title: "Мастер",
        minBins: 10,
        maxBins: 19,
        pricePerBin: 300000,
        description:
            "Для расширенной структуры с регулярной сверкой по нескольким организациям.",
    },
    {
        title: "Профессионал",
        minBins: 20,
        maxBins: null,
        pricePerBin: 250000,
        description:
            "Для крупных организаций, филиалов и структур с постоянным объемом проверок.",
    },
    // {
    //     title: "Аудитор*",
    //     minBins: 1,
    //     maxBins: null,
    //     pricePerBin: 250000,
    //     description:
    //         "Специальный тариф для аудиторов при наличии сертификата аудитора.",
    // },
];

const INCLUDED_FEATURES = [
    "Полный функционал системы на всех тарифах",
    "Сверка банковских документов с бухгалтерскими данными",
    "Сверка с кадровыми данными",
    "Просмотр результатов на странице и выгрузка в Excel",
];

function formatMoney(value) {
    if (value === null || value === undefined) return "—";
    return `${new Intl.NumberFormat("ru-RU").format(value)} ₸`;
}

function formatBinRange(minBins, maxBins) {
    if (maxBins === null) {
        return `от ${minBins} БИН`;
    }

    if (minBins === maxBins) {
        return `${minBins} БИН`;
    }

    return `от ${minBins} до ${maxBins} БИН`;
}

function getPlanByBins(count) {
    return PRICING_PLANS.find((plan) => {
        if (plan.maxBins === null) {
            return count >= plan.minBins;
        }
        return count >= plan.minBins && count <= plan.maxBins;
    });
}

function clampBins(value) {
    if (Number.isNaN(value)) return MIN_BINS;
    return Math.min(MAX_BINS, Math.max(MIN_BINS, value));
}

function HomePricing() {
    const [bins, setBins] = useState(1);

    const parsedBins =
        bins === "" ? MIN_BINS : clampBins(Number(bins));

    const selectedPlan = useMemo(() => getPlanByBins(parsedBins), [parsedBins]);

    const calculation = useMemo(() => {
        if (!selectedPlan) {
            return {
                planName: "Индивидуально",
                pricePerBin: null,
                total: null,
                note: "Данный расчет имеет информативный и ориентировочный характер. Итоговую актуальную цену вы узнаете при подаче заявки.",
            };
        }

        return {
            planName: selectedPlan.title,
            pricePerBin: selectedPlan.pricePerBin,
            total: parsedBins * selectedPlan.pricePerBin,
            note: "Данный расчет имеет информативный и ориентировочный характер. Итоговую актуальную цену вы узнаете при подаче заявки.",
        };
    }, [parsedBins, selectedPlan]);

    const handleDecrease = () => {
        setBins((prev) => {
            const current = prev === "" ? MIN_BINS : Number(prev);
            return clampBins(current - 1);
        });
    };

    const handleIncrease = () => {
        setBins((prev) => {
            const current = prev === "" ? MIN_BINS : Number(prev);
            return clampBins(current + 1);
        });
    };

    const handleBinsChange = (e) => {
        const value = e.target.value;

        if (value === "") {
            setBins("");
            return;
        }

        if (!/^\d+$/.test(value)) {
            return;
        }

        const numericValue = Number(value);
        setBins(clampBins(numericValue));
    };

    const handleBinsBlur = () => {
        if (bins === "") {
            setBins(MIN_BINS);
            return;
        }

        setBins(clampBins(Number(bins)));
    };

    return (
        <Container maxWidth="xl" sx={{ py: { xs: 6, md: 8 } }}>
            <Box textAlign="center" sx={{ mb: { xs: 4, md: 5 } }}>
                <Typography variant="h3" sx={sectionTitleSx}>
                    Тарифы
                </Typography>

                <Typography sx={{ ...sectionSubtitleSx, maxWidth: 920, mx: "auto" }}>
                    Все тарифы включают одинаковый функционал системы. Разница только
                    в количестве подключаемых БИН и стоимости за 1 БИН, что позволяет
                    подобрать подходящий вариант в зависимости от масштаба организации.
                </Typography>
            </Box>

            <Box
                sx={{
                    display: "grid",
                    gridTemplateColumns: { xs: "1fr", lg: "1.16fr 0.84fr" },
                    gap: { xs: 2, md: 2.5 },
                    alignItems: "stretch",
                }}
            >
                <Paper
                    elevation={0}
                    sx={{
                        p: { xs: 2.2, md: 3 },
                        borderRadius: 4,
                        border: "1px solid rgba(148, 163, 184, 0.18)",
                        bgcolor: "#ffffff",
                        boxShadow: "0 14px 34px rgba(15, 23, 42, 0.06)",
                        position: "relative",
                        overflow: "hidden",
                    }}
                >
                    <Box
                        sx={{
                            position: "absolute",
                            top: -120,
                            right: -120,
                            width: 280,
                            height: 280,
                            borderRadius: "50%",
                            background:
                                "radial-gradient(circle, rgba(37,99,235,0.10) 0%, rgba(37,99,235,0.02) 68%, rgba(37,99,235,0) 100%)",
                            pointerEvents: "none",
                        }}
                    />

                    <Stack
                        direction="row"
                        spacing={1.2}
                        alignItems="center"
                        sx={{ mb: 1.5, position: "relative", zIndex: 1 }}
                    >
                        <Box
                            sx={{
                                width: 42,
                                height: 42,
                                borderRadius: 2.5,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                bgcolor: "rgba(37, 99, 235, 0.08)",
                                color: "#2563eb",
                                flexShrink: 0,
                            }}
                        >
                            <BusinessCenterOutlinedIcon />
                        </Box>

                        <Box>
                            <Typography
                                sx={{
                                    fontWeight: 800,
                                    fontSize: { xs: "1.1rem", md: "1.2rem" },
                                    color: "#0f172a",
                                    lineHeight: 1.2,
                                }}
                            >
                                Тарифная сетка
                            </Typography>

                            <Typography
                                sx={{
                                    mt: 0.4,
                                    color: "#64748b",
                                    fontSize: "0.94rem",
                                    lineHeight: 1.55,
                                }}
                            >
                                Выберите подходящий диапазон по количеству подключаемых БИН.
                            </Typography>
                        </Box>
                    </Stack>

                    <Divider sx={{ mb: 2, position: "relative", zIndex: 1 }} />

                    <Stack spacing={1.15} sx={{ position: "relative", zIndex: 1 }}>
                        {PRICING_PLANS.map((plan) => (
                            <Box
                                key={plan.title}
                                sx={{
                                    p: { xs: 1.5, md: 1.75 },
                                    borderRadius: 3,
                                    border: "1px solid rgba(148, 163, 184, 0.16)",
                                    bgcolor:
                                        selectedPlan?.title === plan.title
                                            ? "rgba(37, 99, 235, 0.04)"
                                            : "rgba(248, 250, 252, 0.92)",
                                    borderColor:
                                        selectedPlan?.title === plan.title
                                            ? "rgba(37, 99, 235, 0.26)"
                                            : "rgba(148, 163, 184, 0.16)",
                                    transition:
                                        "background-color 0.2s ease, border-color 0.2s ease, transform 0.2s ease",
                                    "&:hover": {
                                        transform: "translateY(-1px)",
                                    },
                                }}
                            >
                                <Stack
                                    direction={{ xs: "column", sm: "row" }}
                                    spacing={1.2}
                                    justifyContent="space-between"
                                    alignItems={{ xs: "flex-start", sm: "center" }}
                                    sx={{ mb: 0.75 }}
                                >
                                    <Stack
                                        direction={{ xs: "column", sm: "row" }}
                                        spacing={1}
                                        alignItems={{ xs: "flex-start", sm: "center" }}
                                    >
                                        <Typography
                                            sx={{
                                                fontWeight: 800,
                                                fontSize: "1rem",
                                                color: "#0f172a",
                                                lineHeight: 1.2,
                                            }}
                                        >
                                            {plan.title}
                                        </Typography>

                                        <Chip
                                            label={formatBinRange(plan.minBins, plan.maxBins)}
                                            size="small"
                                            sx={{
                                                height: 27,
                                                borderRadius: 999,
                                                bgcolor: "rgba(37, 99, 235, 0.08)",
                                                color: "#2563eb",
                                                fontWeight: 700,
                                                "& .MuiChip-label": {
                                                    px: 1.2,
                                                },
                                            }}
                                        />
                                    </Stack>

                                    <Typography
                                        sx={{
                                            fontWeight: 800,
                                            fontSize: "0.98rem",
                                            color: "#2563eb",
                                            whiteSpace: "nowrap",
                                        }}
                                    >
                                        {formatMoney(plan.pricePerBin)} / 1 БИН
                                    </Typography>
                                </Stack>

                                <Typography
                                    sx={{
                                        color: "#475569",
                                        fontSize: "0.92rem",
                                        lineHeight: 1.6,
                                    }}
                                >
                                    {plan.description}
                                </Typography>
                            </Box>
                        ))}

                        <Box
                            sx={{
                                mt: 0.4,
                                p: { xs: 1.6, md: 1.85 },
                                borderRadius: 3.2,
                                border: "1px solid rgba(37, 99, 235, 0.16)",
                                background:
                                    "linear-gradient(180deg, rgba(239,246,255,0.92) 0%, rgba(248,250,252,0.96) 100%)",
                                boxShadow: "0 10px 24px rgba(37, 99, 235, 0.06)",
                            }}
                        >
                            <Stack
                                direction={{ xs: "column", sm: "row" }}
                                spacing={1.2}
                                justifyContent="space-between"
                                alignItems={{ xs: "flex-start", sm: "center" }}
                                sx={{ mb: 0.8 }}
                            >
                                <Stack direction="row" spacing={1} alignItems="center">
                                    <Box
                                        sx={{
                                            width: 36,
                                            height: 36,
                                            borderRadius: 2.2,
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            bgcolor: "rgba(37, 99, 235, 0.10)",
                                            color: "#2563eb",
                                            flexShrink: 0,
                                        }}
                                    >
                                        <AccountBalanceOutlinedIcon sx={{ fontSize: 20 }} />
                                    </Box>

                                    <Typography
                                        sx={{
                                            fontWeight: 800,
                                            fontSize: "1rem",
                                            color: "#0f172a",
                                            lineHeight: 1.2,
                                        }}
                                    >
                                        Гос
                                    </Typography>
                                </Stack>

                                <Chip
                                    label="индивидуальные условия"
                                    size="small"
                                    sx={{
                                        height: 28,
                                        borderRadius: 999,
                                        bgcolor: "rgba(37, 99, 235, 0.08)",
                                        color: "#2563eb",
                                        fontWeight: 700,
                                        "& .MuiChip-label": {
                                            px: 1.25,
                                        },
                                    }}
                                />
                            </Stack>

                            <Typography
                                sx={{
                                    color: "#475569",
                                    fontSize: "0.92rem",
                                    lineHeight: 1.65,
                                }}
                            >
                                Для государственных органов и учреждений. Подключение,
                                объем доступа и условия использования подтверждаются
                                индивидуально и рассчитываются отдельно.
                            </Typography>
                        </Box>
                    </Stack>
                </Paper>

                <Paper
                    elevation={0}
                    sx={{
                        p: { xs: 1.8, md: 2.3 },
                        borderRadius: 5,
                        background:
                            "linear-gradient(180deg, #0f172a 0%, #172554 100%)",
                        color: "white",
                        boxShadow: "0 22px 52px rgba(15, 23, 42, 0.28)",
                        display: "flex",
                        flexDirection: "column",
                        position: "relative",
                        overflow: "hidden",
                    }}
                >
                    <Box
                        sx={{
                            position: "absolute",
                            top: -120,
                            right: -120,
                            width: 320,
                            height: 320,
                            borderRadius: "50%",
                            background:
                                "radial-gradient(circle, rgba(59,130,246,0.22) 0%, rgba(59,130,246,0.04) 68%, rgba(59,130,246,0) 100%)",
                            pointerEvents: "none",
                        }}
                    />

                    <Stack spacing={1.8} sx={{ position: "relative", zIndex: 1 }}>
                        <Stack
                            direction="row"
                            justifyContent="space-between"
                            alignItems="center"
                        >
                            <Box>
                                <Typography
                                    sx={{
                                        color: "rgba(255,255,255,0.70)",
                                        fontSize: 12,
                                        lineHeight: 1.45,
                                        mb: 0.4,
                                    }}
                                >
                                    Быстрый ориентировочный расчет
                                </Typography>
                                <Typography
                                    sx={{
                                        fontSize: { xs: "1rem", md: "1.1rem" },
                                        fontWeight: 800,
                                        lineHeight: 1.2,
                                    }}
                                >
                                    Калькулятор тарифа
                                </Typography>
                            </Box>

                            <Box
                                sx={{
                                    width: 42,
                                    height: 42,
                                    borderRadius: "50%",
                                    bgcolor: "rgba(255,255,255,0.12)",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                }}
                            >
                                <CalculateOutlinedIcon sx={{ fontSize: 20 }} />
                            </Box>
                        </Stack>

                        <Paper
                            elevation={0}
                            sx={{
                                p: { xs: 1.35, md: 1.5 },
                                borderRadius: 3.2,
                                bgcolor: "rgba(255,255,255,0.08)",
                                border: "1px solid rgba(255,255,255,0.10)",
                            }}
                        >
                            <Stack
                                direction={{ xs: "column", sm: "row" }}
                                spacing={{ xs: 1.15, sm: 1.5 }}
                                alignItems={{ xs: "stretch", sm: "center" }}
                                justifyContent="space-between"
                            >
                                <Typography
                                    sx={{
                                        color: "#ffffff",
                                        fontWeight: 800,
                                        fontSize: { xs: "1.02rem", md: "1.1rem" },
                                        lineHeight: 1.2,
                                        minWidth: { sm: 155, md: 175 },
                                    }}
                                >
                                    Количество БИН
                                </Typography>

                                <Box
                                    sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: { xs: "space-between", sm: "flex-end" },
                                        gap: 1,
                                        width: "100%",
                                    }}
                                >
                                    <IconButton
                                        onClick={handleDecrease}
                                        disabled={parsedBins <= MIN_BINS}
                                        sx={{
                                            width: 46,
                                            height: 46,
                                            borderRadius: 999,
                                            bgcolor: "rgba(255,255,255,0.14)",
                                            color: "#ffffff",
                                            border: "1px solid rgba(255,255,255,0.14)",
                                            boxShadow: "inset 0 1px 0 rgba(255,255,255,0.08)",
                                            transition: "all 0.2s ease",
                                            flexShrink: 0,
                                            "&:hover": {
                                                bgcolor: "rgba(255,255,255,0.20)",
                                                transform: "translateY(-1px)",
                                            },
                                            "&.Mui-disabled": {
                                                color: "rgba(255,255,255,0.28)",
                                                bgcolor: "rgba(255,255,255,0.06)",
                                                borderColor: "rgba(255,255,255,0.08)",
                                            },
                                        }}
                                    >
                                        <RemoveIcon sx={{ fontSize: 22 }} />
                                    </IconButton>

                                    <Box
                                        sx={{
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            px: 0.75,
                                            py: 0.5,
                                            borderRadius: 999,
                                            bgcolor: "rgba(255,255,255,0.96)",
                                            border: "1px solid rgba(148, 163, 184, 0.26)",
                                            boxShadow: "0 8px 22px rgba(15, 23, 42, 0.10)",
                                            minWidth: { xs: 118, sm: 126, md: 136 },
                                        }}
                                    >
                                        <TextField
                                            type="number"
                                            value={bins}
                                            onChange={handleBinsChange}
                                            onBlur={handleBinsBlur}
                                            inputProps={{
                                                min: MIN_BINS,
                                                max: MAX_BINS,
                                                style: {
                                                    textAlign: "center",
                                                    padding: 0,
                                                },
                                            }}
                                            variant="standard"
                                            sx={{
                                                width: { xs: 64, sm: 72 },
                                                "& .MuiInputBase-root": {
                                                    height: 34,
                                                    fontSize: "1.08rem",
                                                    fontWeight: 800,
                                                    color: "#0f172a",
                                                },
                                                "& .MuiInputBase-input": {
                                                    textAlign: "center",
                                                    p: 0,
                                                    MozAppearance: "textfield",
                                                },
                                                "& .MuiInput-underline:before": {
                                                    display: "none",
                                                },
                                                "& .MuiInput-underline:after": {
                                                    display: "none",
                                                },
                                                "& .MuiInput-underline:hover:not(.Mui-disabled):before": {
                                                    display: "none",
                                                },
                                                "& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button":
                                                    {
                                                        WebkitAppearance: "none",
                                                        margin: 0,
                                                    },
                                            }}
                                        />

                                    </Box>

                                    <IconButton
                                        onClick={handleIncrease}
                                        disabled={parsedBins >= MAX_BINS}
                                        sx={{
                                            width: 46,
                                            height: 46,
                                            borderRadius: 999,
                                            bgcolor: "rgba(255,255,255,0.14)",
                                            color: "#ffffff",
                                            border: "1px solid rgba(255,255,255,0.14)",
                                            boxShadow: "inset 0 1px 0 rgba(255,255,255,0.08)",
                                            transition: "all 0.2s ease",
                                            flexShrink: 0,
                                            "&:hover": {
                                                bgcolor: "rgba(255,255,255,0.20)",
                                                transform: "translateY(-1px)",
                                            },
                                            "&.Mui-disabled": {
                                                color: "rgba(255,255,255,0.28)",
                                                bgcolor: "rgba(255,255,255,0.06)",
                                                borderColor: "rgba(255,255,255,0.08)",
                                            },
                                        }}
                                    >
                                        <AddIcon sx={{ fontSize: 22 }} />
                                    </IconButton>
                                </Box>
                            </Stack>
                        </Paper>

                        <Paper
                            elevation={0}
                            sx={{
                                p: 1.6,
                                borderRadius: 3.2,
                                bgcolor: "rgba(255,255,255,0.08)",
                                border: "1px solid rgba(255,255,255,0.10)",
                                boxShadow: "inset 0 1px 0 rgba(255,255,255,0.04)",
                            }}
                        >
                            <Stack spacing={1.25}>
                                <Box>
                                    <Typography
                                        sx={{
                                            color: "rgba(255,255,255,0.68)",
                                            fontSize: 12,
                                            lineHeight: 1.4,
                                            mb: 0.35,
                                        }}
                                    >
                                        Подходящий тариф
                                    </Typography>
                                    <Typography
                                        sx={{
                                            fontWeight: 900,
                                            fontSize: { xs: "1.24rem", md: "1.34rem" },
                                            lineHeight: 1.12,
                                            color: "#ffffff",
                                            letterSpacing: "-0.02em",
                                        }}
                                    >
                                        {calculation.planName}
                                    </Typography>
                                </Box>

                                <Divider
                                    sx={{
                                        borderColor: "rgba(255,255,255,0.10)",
                                    }}
                                />

                                <Stack
                                    direction="row"
                                    justifyContent="space-between"
                                    spacing={2}
                                >
                                    <Typography
                                        sx={{
                                            color: "rgba(255,255,255,0.72)",
                                            fontSize: "0.94rem",
                                        }}
                                    >
                                        Цена за 1 БИН
                                    </Typography>
                                    <Typography
                                        sx={{
                                            fontWeight: 800,
                                            fontSize: "0.99rem",
                                            color: "#ffffff",
                                            textAlign: "right",
                                        }}
                                    >
                                        {formatMoney(calculation.pricePerBin)}
                                    </Typography>
                                </Stack>

                                <Stack
                                    direction="row"
                                    justifyContent="space-between"
                                    spacing={2}
                                >
                                    <Typography
                                        sx={{
                                            color: "rgba(255,255,255,0.72)",
                                            fontSize: "0.94rem",
                                        }}
                                    >
                                        Количество БИН
                                    </Typography>
                                    <Typography
                                        sx={{
                                            fontWeight: 800,
                                            fontSize: "0.99rem",
                                            color: "#ffffff",
                                        }}
                                    >
                                        {parsedBins}
                                    </Typography>
                                </Stack>

                                <Stack
                                    direction="row"
                                    justifyContent="space-between"
                                    spacing={2}
                                    sx={{
                                        p: 1.1,
                                        borderRadius: 2.5,
                                        bgcolor: "rgba(147,197,253,0.10)",
                                        border: "1px solid rgba(147,197,253,0.12)",
                                    }}
                                >
                                    <Typography
                                        sx={{
                                            color: "#dbeafe",
                                            fontSize: "0.95rem",
                                            fontWeight: 700,
                                        }}
                                    >
                                        Итоговая стоимость
                                    </Typography>
                                    <Typography
                                        sx={{
                                            fontWeight: 900,
                                            fontSize: "1.14rem",
                                            color: "#93c5fd",
                                            textAlign: "right",
                                            letterSpacing: "-0.02em",
                                        }}
                                    >
                                        {formatMoney(calculation.total)}
                                    </Typography>
                                </Stack>

                                <Typography
                                    sx={{
                                        pt: 0.35,
                                        color: "rgba(255,255,255,0.72)",
                                        fontSize: "0.88rem",
                                        lineHeight: 1.62,
                                    }}
                                >
                                    {calculation.note}
                                </Typography>
                            </Stack>
                        </Paper>

                        <Stack spacing={0.9}>
                            {INCLUDED_FEATURES.map((feature) => (
                                <Stack
                                    key={feature}
                                    direction="row"
                                    spacing={1}
                                    alignItems="flex-start"
                                >
                                    <CheckCircleIcon
                                        sx={{
                                            fontSize: 18,
                                            color: "#60a5fa",
                                            mt: "2px",
                                            flexShrink: 0,
                                        }}
                                    />
                                    <Typography
                                        sx={{
                                            color: "rgba(255,255,255,0.84)",
                                            fontSize: "0.92rem",
                                            lineHeight: 1.55,
                                        }}
                                    >
                                        {feature}
                                    </Typography>
                                </Stack>
                            ))}
                        </Stack>


                        <Button
                            component={RouterLink}
                            to="/contacts"
                            variant="contained"
                            endIcon={<ArrowOutwardIcon />}
                            sx={{
                                mt: 0.6,
                                alignSelf: "flex-start",
                                px: 3,
                                py: 1.15,
                                borderRadius: 3,
                                textTransform: "none",
                                fontWeight: 700,
                                fontSize: "0.95rem",
                                boxShadow: "0 12px 24px rgba(37, 99, 235, 0.24)",
                                bgcolor: "#2563eb",
                                "&:hover": {
                                    bgcolor: "#1d4ed8",
                                    boxShadow: "0 12px 24px rgba(37, 99, 235, 0.30)",
                                },
                            }}
                        >
                            Оставить заявку
                        </Button>

                    </Stack>
                </Paper>
            </Box>
        </Container>
    );
}

export default HomePricing;